// frontend/src/components/SocialReportBuilder.js
//
// Builds a monthly social media report in the browser, on
// /tools/social-media-report-template.
//
// MEASURED 2026-08-16/17: `social media report` and `social media reporting` are
// 590/mo each at KD 16 with a CPC of $40.70; `social media report template` and
// `social media reporting template` 480 each at $7.50; `social media audit
// template` 480 at KD 9. Family total 3,160 searches, every term KD 0-17, and
// crucially NO AI OVERVIEW — a template is a file, not a fact.
//
// The wall is low: hubspot.com/resources/templates/social-media-report has 5
// referring domains, canva 4, adobe 7. They rank on brand, not links.
//
// THE DIFFERENTIATOR IS THAT IT CALCULATES. Every competing result — HubSpot,
// Canva, Adobe, Smartsheet — hands you a blank grid to type numbers into. This
// works out the derived metrics, and does it with EACH PLATFORM'S OWN engagement
// formula rather than one generic formula, which is the same correction the
// engagement rate calculator makes. A report that computes Instagram's rate with
// TikTok's formula is wrong in a way nobody notices until they quote it.
//
// Entirely client-side. Nothing uploaded, nothing stored, no endpoint.

import React, { useState, useMemo, useRef, useEffect } from 'react'
import { Row, Col, Form, Button, Table } from 'react-bootstrap'
import { Download, Clipboard, ClipboardCheck, GraphUp } from 'react-bootstrap-icons'

// Engagement is defined differently per network — see the engagement rate
// calculator for the sourcing. `counts` names what that platform actually adds up.
const PLATFORMS = [
  { key: 'instagram', label: 'Instagram', counts: 'likes + comments' },
  { key: 'tiktok',    label: 'TikTok',    counts: 'likes + comments + shares + saves' },
  { key: 'facebook',  label: 'Facebook',  counts: 'reactions + comments + shares' },
  { key: 'linkedin',  label: 'LinkedIn',  counts: 'reactions + comments + reposts' },
  { key: 'x',         label: 'X',         counts: 'likes + reposts' },
  { key: 'youtube',   label: 'YouTube',   counts: 'likes + comments' },
]

const num = v => {
  const n = parseFloat(String(v).replace(/[, ]/g, ''))
  return Number.isFinite(n) && n >= 0 ? n : 0
}
const pct = (a, b) => (b > 0 ? (a / b) * 100 : null)
const fmt = n => n == null ? '—' : n >= 100 ? n.toFixed(0) : n >= 10 ? n.toFixed(1) : n.toFixed(2)
const int = n => (n || 0).toLocaleString()

export default function SocialReportBuilder () {
  const now = new Date()
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`)
  const [active, setActive] = useState(['instagram', 'facebook'])
  const [data, setData] = useState({})
  const [copied, setCopied] = useState(false)
  const urlRef = useRef(null)
  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current) }, [])

  const set = (p, field, v) => setData(d => ({ ...d, [p]: { ...(d[p] || {}), [field]: v } }))
  const toggle = k => setActive(a => a.includes(k) ? a.filter(x => x !== k) : [...a, k])

  const rows = useMemo(() => active.map(k => {
    const p = PLATFORMS.find(x => x.key === k)
    const d = data[k] || {}
    const start = num(d.start), end = num(d.end)
    const posts = num(d.posts), reach = num(d.reach), eng = num(d.eng)
    return {
      ...p,
      start, end, posts, reach, eng,
      growth: end - start,
      growthPct: pct(end - start, start),
      erFollower: pct(eng, end || start),
      erReach: pct(eng, reach),
      perPost: posts > 0 ? eng / posts : null,
    }
  }), [active, data])

  const totals = useMemo(() => ({
    posts: rows.reduce((s, r) => s + r.posts, 0),
    eng: rows.reduce((s, r) => s + r.eng, 0),
    reach: rows.reduce((s, r) => s + r.reach, 0),
    growth: rows.reduce((s, r) => s + r.growth, 0),
  }), [rows])

  const asText = useMemo(() => {
    const L = [`Social media report — ${month}`, '']
    L.push(`Posts published: ${int(totals.posts)}`)
    L.push(`Total engagements: ${int(totals.eng)}`)
    if (totals.reach) L.push(`Total reach: ${int(totals.reach)}`)
    L.push(`Net follower change: ${totals.growth >= 0 ? '+' : ''}${int(totals.growth)}`)
    L.push('')
    for (const r of rows) {
      L.push(`${r.label}`)
      L.push(`  Followers: ${int(r.start)} -> ${int(r.end)} (${r.growth >= 0 ? '+' : ''}${int(r.growth)}${r.growthPct != null ? `, ${fmt(r.growthPct)}%` : ''})`)
      L.push(`  Posts: ${int(r.posts)}   Engagements: ${int(r.eng)}`)
      L.push(`  Engagement rate by follower: ${fmt(r.erFollower)}%`)
      if (r.erReach != null) L.push(`  Engagement rate by reach: ${fmt(r.erReach)}%`)
      if (r.perPost != null) L.push(`  Average engagements per post: ${fmt(r.perPost)}`)
      L.push(`  (${r.label} counts ${r.counts})`)
      L.push('')
    }
    return L.join('\n')
  }, [rows, totals, month])

  const csv = useMemo(() => {
    const head = ['platform', 'month', 'followers_start', 'followers_end', 'follower_growth',
                  'follower_growth_pct', 'posts', 'reach', 'engagements',
                  'engagement_rate_by_follower_pct', 'engagement_rate_by_reach_pct',
                  'avg_engagements_per_post', 'engagement_counted_as']
    const lines = [head.join(',')]
    for (const r of rows) {
      lines.push([r.label, month, r.start, r.end, r.growth,
        r.growthPct == null ? '' : r.growthPct.toFixed(2), r.posts, r.reach, r.eng,
        r.erFollower == null ? '' : r.erFollower.toFixed(2),
        r.erReach == null ? '' : r.erReach.toFixed(2),
        r.perPost == null ? '' : r.perPost.toFixed(1),
        `"${r.counts}"`].join(','))
    }
    return lines.join('\n')
  }, [rows, month])

  const copy = async () => {
    try { await navigator.clipboard.writeText(asText); setCopied(true); setTimeout(() => setCopied(false), 2200) }
    catch { /* clipboard unavailable */ }
  }
  const download = () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob); urlRef.current = url
    const a = document.createElement('a'); a.href = url
    a.download = `social-media-report-${month}.csv`
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
  }

  const hasData = rows.some(r => r.eng > 0 || r.end > 0)

  return (
    <div className='p-4 p-md-5 rounded-3 shadow-card' style={{ background: '#fff', border: '1px solid #e5e7eb' }}>
      <h2 className='fw-bold h4 mb-2'>
        <GraphUp className='text-primary me-2' />
        Build your report
      </h2>
      <p className='text-muted mb-4'>
        Enter last month's numbers and this works out the rest — including engagement rate using
        <strong> each platform's own formula</strong>, not one generic one. Free, nothing uploaded.
      </p>

      <Row className='g-3 mb-3'>
        <Col md={4}>
          <Form.Label className='fw-semibold small'>Reporting month</Form.Label>
          <Form.Control type='month' value={month} onChange={e => setMonth(e.target.value)} />
        </Col>
        <Col md={8}>
          <Form.Label className='fw-semibold small'>Networks in this report</Form.Label>
          <div className='d-flex flex-wrap gap-2'>
            {PLATFORMS.map(p => (
              <Button key={p.key} size='sm' variant={active.includes(p.key) ? 'primary' : 'outline-secondary'}
                      onClick={() => toggle(p.key)}>{p.label}</Button>
            ))}
          </div>
        </Col>
      </Row>

      {active.length === 0 && <p className='text-danger small'>Pick at least one network.</p>}

      {rows.map(r => (
        <div key={r.key} className='p-3 rounded-3 mb-3' style={{ border: '1px solid #e5e7eb' }}>
          <div className='fw-semibold mb-1'>{r.label}</div>
          <div className='text-muted small mb-3'>Engagement here means {r.counts}.</div>
          <Row className='g-2'>
            {[['start', 'Followers at start'], ['end', 'Followers at end'], ['posts', 'Posts published'],
              ['reach', 'Reach (optional)'], ['eng', 'Total engagements']].map(([f, label]) => (
              <Col xs={6} md key={f}>
                <Form.Control inputMode='numeric' placeholder={label} aria-label={`${r.label} ${label}`}
                              value={(data[r.key] || {})[f] || ''} onChange={e => set(r.key, f, e.target.value)} />
                <Form.Text className='text-muted' style={{ fontSize: 11 }}>{label}</Form.Text>
              </Col>
            ))}
          </Row>
        </div>
      ))}

      {hasData && (
        <>
          <h3 className='fw-bold h6 text-uppercase text-muted mt-4 mb-3' style={{ letterSpacing: '.05em' }}>
            Your report — {month}
          </h3>

          <Row className='g-3 mb-3'>
            {[['Posts published', int(totals.posts)],
              ['Total engagements', int(totals.eng)],
              ['Net follower change', `${totals.growth >= 0 ? '+' : ''}${int(totals.growth)}`]].map(([l, v]) => (
              <Col md={4} key={l}>
                <div className='p-3 rounded-3 h-100' style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                  <div className='text-muted small text-uppercase' style={{ letterSpacing: '.04em' }}>{l}</div>
                  <div className='fw-bold text-primary' style={{ fontSize: '1.8rem', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>{v}</div>
                </div>
              </Col>
            ))}
          </Row>

          <div className='table-responsive mb-3'>
            <Table bordered className='align-middle mb-0'>
              <thead className='table-light'>
                <tr>
                  <th>Network</th><th>Followers</th><th>Growth</th>
                  <th>Posts</th><th>ER by follower</th><th>ER by reach</th><th>Per post</th>
                </tr>
              </thead>
              <tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
                {rows.map(r => (
                  <tr key={r.key}>
                    <td className='fw-semibold'>{r.label}</td>
                    <td>{int(r.start)} → {int(r.end)}</td>
                    <td className={r.growth >= 0 ? 'text-success' : 'text-danger'}>
                      {r.growth >= 0 ? '+' : ''}{int(r.growth)}
                      {r.growthPct != null && <span className='text-muted small'> ({fmt(r.growthPct)}%)</span>}
                    </td>
                    <td>{int(r.posts)}</td>
                    <td className='fw-semibold text-primary'>{fmt(r.erFollower)}%</td>
                    <td>{fmt(r.erReach)}%</td>
                    <td>{fmt(r.perPost)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className='d-flex gap-2 flex-wrap'>
            <Button variant='primary' onClick={download}>
              <Download className='me-1' />Download CSV
            </Button>
            <Button variant={copied ? 'success' : 'outline-secondary'} onClick={copy}>
              {copied ? <><ClipboardCheck className='me-1' />Copied</> : <><Clipboard className='me-1' />Copy as text</>}
            </Button>
          </div>

          <p className='text-muted small mt-3'>
            Engagement rate by follower is the benchmarking number; by reach is what a client or
            brand partner usually means. They differ a lot on a post that travelled beyond your
            followers — see the{' '}
            <a href='/tools/engagement-rate-calculator'>engagement rate calculator</a> for why.
          </p>

          <div className='p-4 rounded-3 mt-4' style={{ background: 'linear-gradient(135deg,#1d4ed8,#2563eb)', color: '#fff' }}>
            <h3 className='fw-bold h5 mb-2'>The numbers are the easy half</h3>
            <p className='mb-3' style={{ color: 'rgba(255,255,255,.88)' }}>
              At Greatly Social you can schedule the month this report is measuring — eight
              networks, one calendar — so next month's numbers come from a plan rather than
              whatever you managed to post.
            </p>
            <Button variant='light' href='/register' className='fw-semibold me-2 mb-2'>
              Start using Greatly Social free
            </Button>
            <Button variant='outline-light' href='/tools/content-calendar-template' className='mb-2'>
              Plan next month
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
